import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Card, Button, Dropdown, Menu, Select,
} from 'antd';
import { useMediaQuery } from 'react-responsive';
import Qty from 'js-quantities';
import { Link, useParams } from 'react-router-dom';

import ParameterChart from './ParameterChart';
import ConnectionStatus from '../ConnectionStatus';
import QartodLabel from '../QartodLabel';
import ParameterLog from './ParameterLog';

import DataRangeSelect from '../DataRangeSelect';
import LastUpdated from '../LastUpdated';
import { getParameterObsLatestDate, getPreferredUnit } from '../../../../utils';
import { brandingConfig } from '../../../../config';
import ShowParameterToggle from './ShowParameterToggle';
import { OptionsIcon } from '../../../../components/icons';
import { getDisplayName } from '../utils';
import UserContext from '../../../../contexts/UserContext';

const ParameterCard = (props) => {
  const {
    parameter,
    parameterConfigurationsResult,
    obsDataV2,
    getQartodResults,
    onObsDateRangeChange,
    obsLatestData,
    isLoading,
    selectedTimestamp,
  } = props;

  const { unitPreferences } = useContext(UserContext);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [logView, setLogView] = useState(false);
  const [dataPointsVisible, setDataPointsVisible] = useState(true);
  const [linesVisible, setLinesVisible] = useState(false);
  const [filterEnabled, setFilterEnabled] = useState(true);

  const { obsDatasetId: obsDatasetIdParam } = useParams();
  const obsDatasetId = parseInt(obsDatasetIdParam, 10);

  const parameterConfig = parameterConfigurationsResult?.find((param) => param.standard_name === parameter.standard_name);

  const displayName = getDisplayName(parameterConfigurationsResult, parameter);

  const canonicalUnit = parameterConfig?.units.find((u) => u.id === parameterConfig.canonical_unit_id);
  const preferredUnit = getPreferredUnit(canonicalUnit, parameterConfig, unitPreferences);
  const preferredUnitConfig = parameterConfig?.units.find((u) => u.id === preferredUnit);

  const displayUnit = preferredUnitConfig?.symbol || canonicalUnit?.symbol || '';

  const latestTimestamp = getParameterObsLatestDate(obsLatestData, obsDatasetId, parameter?.parameter_id);

  const rawObs = obsDataV2?.parameter_obs?.values?.[parameter?.parameter_id] ?? [];
  const rawQartod = obsDataV2?.parameter_obs?.qartod?.[parameter?.parameter_id];
  const rawTimestamps = obsDataV2?.timestamps;

  const alertsMin = filterEnabled ? parameterConfig?.alerts?.threshold_min : null;
  const alertsMax = filterEnabled ? parameterConfig?.alerts?.threshold_max : null;

  const filteredIndices = rawObs.reduce((acc, val, i) => {
    if (val === null || val === undefined) { acc.push(i); return acc; }
    if (alertsMin != null && val < alertsMin) return acc;
    if (alertsMax != null && val > alertsMax) return acc;
    acc.push(i);
    return acc;
  }, []);

  const parameterObs = filteredIndices.map((i) => {
    const o = rawObs[i];
    if (o === null || o === undefined) return o;
    try {
      return Qty(o, canonicalUnit?.js_qty_unit).to(preferredUnitConfig?.js_qty_unit).scalar.toFixed(2);
    } catch {
      return o;
    }
  });
  const parameterQartod = rawQartod ? filteredIndices.map((i) => rawQartod[i]) : rawQartod;
  const timestamps = rawTimestamps ? filteredIndices.map((i) => rawTimestamps[i]) : rawTimestamps;
  const qartodResults = obsDataV2?.parameter_obs?.qartod?.[parameter?.parameter_id];

  if (logView) {
    return (
      <ParameterLog
        parameterObs={parameterObs}
        timestamps={timestamps}
        displayName={displayName}
        displayUnit={displayUnit}
        qartodResults={qartodResults}
        getQartodResults={getQartodResults}
      />
    );
  }

  const optionsMenu = (
    <Menu>
      <Menu.Item onClick={() => setDataPointsVisible((prev) => !prev)}>
        {dataPointsVisible ? 'Hide data points' : 'Show data points'}
      </Menu.Item>
      <Menu.Item onClick={() => setLinesVisible((prev) => !prev)}>
        {linesVisible ? 'Hide lines' : 'Show lines'}
      </Menu.Item>
    </Menu>
  );

  return (
    <Card
      // variant="borderless"
      title={(
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            {/* TODO: icon */}
            <Link
              to={`/data-console/manage/${obsDatasetId}/parameters/${parameter.parameter_id}`}
              style={{
                color: 'white',
              }}
            >
              {displayName}
            </Link>
            <ShowParameterToggle parameter={parameter} />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <ConnectionStatus latestTimestamp={latestTimestamp} />
              <LastUpdated latestTimestamp={latestTimestamp} />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
              }}
            >
              {!isMobile && getQartodResults !== undefined && (
                <DataRangeSelect onChange={onObsDateRangeChange} />
              )}
              {!isMobile && (
                <>
                  <Button
                    size="small"
                    onClick={() => setFilterEnabled((prev) => !prev)}
                    style={{
                      backgroundColor: brandingConfig.colors.greyLight,
                      color: brandingConfig.colors.primaryDark,
                      borderRadius: 15,
                      height: 30,
                      border: 'none',
                    }}
                  >
                    {filterEnabled ? 'Filtered' : 'Full'}
                  </Button>
                  <Dropdown
                    overlay={optionsMenu}
                    placement="bottomRight"
                  >
                    <Button
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                      }}
                      icon={(
                        <OptionsIcon
                          style={{
                            color: '#D9E0E4',
                            rotate: '90deg',
                            fontSize: 27,
                          }}
                        />
                      )}
                    />
                  </Dropdown>
                </>
              )}
            </div>
          </div>
          {isMobile && getQartodResults !== undefined && (
            <div style={{ marginTop: 8 }}>
              <DataRangeSelect onChange={onObsDateRangeChange} />
            </div>
          )}
          {isMobile && (
            <div style={{ marginTop: 8 }}>
              <Select
                value={null}
                placeholder="Chart Options"
                onChange={(val) => {
                  if (val === 'toggleFilter') setFilterEnabled((prev) => !prev);
                  if (val === 'toggleDataPoints') setDataPointsVisible((prev) => !prev);
                  if (val === 'toggleLines') setLinesVisible((prev) => !prev);
                }}
                style={{ width: '100%' }}
              >
                <Select.Option value="toggleFilter">
                  {filterEnabled ? 'Show Full Data' : 'Show Filtered Data'}
                </Select.Option>
                <Select.Option value="toggleDataPoints">
                  {dataPointsVisible ? 'Hide Data Points' : 'Show Data Points'}
                </Select.Option>
                <Select.Option value="toggleLines">
                  {linesVisible ? 'Hide Lines' : 'Show Lines'}
                </Select.Option>
              </Select>
            </div>
          )}
        </>
      )}
      bordered={false}
      style={{
        backgroundColor: '#03304b', // TODO: replace with brand var
        border: '1px solid #4f6f81',
        marginBottom: 0,
      }}
      className="parameter-card"
    >
      <ParameterChart
        obs={parameterObs}
        qartod={parameterQartod}
        timestamps={timestamps}
        name={displayName}
        unit={displayUnit}
        getQartodResults={getQartodResults}
        isLoading={isLoading}
        selectedTimestamp={selectedTimestamp}
        dataPointsVisible={dataPointsVisible}
        linesVisible={linesVisible}
      />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 8,
          marginTop: 12,
        }}
      >
        <QartodLabel result={1} />
        <QartodLabel result={2} />
        <QartodLabel result={3} />
        <QartodLabel result={4} />
        <QartodLabel result={9} />
      </div>
      {getQartodResults !== undefined && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 12,
          }}
        >
          <Button
            className="action-button"
            onClick={() => setLogView(true)}
          >
            Log History
          </Button>
        </div>
      )}
    </Card>
  );
};

ParameterCard.propTypes = {
  parameter: PropTypes.object,
  parameterConfigurationsResult: PropTypes.arrayOf(PropTypes.object),
  obsDataV2: PropTypes.object,
  getQartodResults: PropTypes.func,
  onObsDateRangeChange: PropTypes.func,
  obsLatestData: PropTypes.array,
  isLoading: PropTypes.bool,
  selectedTimestamp: PropTypes.number,
};

ParameterCard.defaultProps = {
  parameter: {},
  parameterConfigurationsResult: [],
  obsDataV2: {},
  getQartodResults: undefined,
  onObsDateRangeChange: undefined,
  obsLatestData: [],
  isLoading: false,
  selectedTimestamp: undefined,
};

export default ParameterCard;
